/**
 * 导出笔记为 ZIP 压缩包
 * 包含文案文本和配图
 */

import JSZip from 'jszip'
import type { Note, NoteImage } from '@/types'

// ============================================================
// 工具函数：下载图片（优先原图，CORS失败时用 Canvas）
// ============================================================

/**
 * 下载图片为 Blob
 * 优先使用 fetch 下载原图（保持格式和压缩），CORS 失败时降级为 Canvas
 */
async function fetchImageAsBlob(url: string): Promise<Blob> {
  // 方案1：尝试直接 fetch（保持原格式和压缩）
  try {
    // 添加时间戳避开缓存 + 设置 CORS 模式
    const cacheBuster = `${url}${url.includes('?') ? '&' : '?'}t=${Date.now()}`
    const response = await fetch(cacheBuster, { 
      mode: 'cors',
      cache: 'no-cache'
    })
    
    if (response.ok) {
      const blob = await response.blob()
      console.log(`✅ 直接下载原图: ${(blob.size / 1024).toFixed(1)} KB, ${blob.type}`)
      return blob
    }
  } catch (error) {
    console.warn('⚠️ fetch 失败，降级使用 Canvas', error)
  }

  // 方案2：降级方案 - 使用 Image + Canvas（会损失格式，变大）
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas')
        canvas.width = img.width
        canvas.height = img.height
        const ctx = canvas.getContext('2d')
        if (!ctx) throw new Error('无法创建 Canvas 上下文')
        
        ctx.drawImage(img, 0, 0)
        
        // 尝试导出为 JPEG（更小）
        canvas.toBlob((blob) => {
          if (blob) {
            console.log(`✅ Canvas 导出: ${(blob.size / 1024).toFixed(1)} KB, ${blob.type}`)
            resolve(blob)
          } else {
            reject(new Error('Canvas 转 Blob 失败'))
          }
        }, 'image/jpeg', 0.9) // JPEG 90% 质量
      } catch (error) {
        reject(error)
      }
    }
    
    img.onerror = () => {
      reject(new Error(`图片加载失败: ${url}`))
    }
    
    // 添加时间戳避开浏览器缓存
    const cacheBuster = `${url}${url.includes('?') ? '&' : '?'}t=${Date.now()}`
    img.src = cacheBuster
  })
}

// ============================================================
// 类型定义
// ============================================================

export interface ExportNote {
  note: Note
  images: Array<{ url: string; filename: string }>
  shareImageDataUrl?: string
  shareImageFilename?: string
}

export interface ExportOptions {
  includeText?: boolean      // 是否包含文案
  includeImages?: boolean    // 是否包含图片
  includeShareImage?: boolean // 是否包含分享图
  shareImageDataUrl?: string  // 单篇导出时的分享图（数据 URL）
  shareImageFilename?: string // 分享图文件名
  textFormat?: 'txt' | 'md'  // 文案格式
  folderPerNote?: boolean    // 每篇笔记一个文件夹
}

export interface ExportProgress {
  current: number
  total: number
  message: string
}

// ============================================================
// 文案生成
// ============================================================

/**
 * 生成笔记文案的纯文本内容
 */
export function generateNoteText(note: Note): string {
  const lines: string[] = []
  
  // 标题
  lines.push(`【${note.title}】`)
  lines.push('')
  
  // 正文
  lines.push(note.content)
  lines.push('')
  
  // 话题标签
  if (note.hashtags.length > 0) {
    lines.push(note.hashtags.map(tag => `#${tag}`).join(' '))
  }
  
  return lines.join('\n')
}

/**
 * 生成笔记文案的 Markdown 内容
 */
export function generateNoteMarkdown(note: Note): string {
  const lines: string[] = []
  
  // 标题
  lines.push(`# ${note.title}`)
  lines.push('')
  
  // 正文（保留原有格式）
  lines.push(note.content)
  lines.push('')
  
  // 话题标签
  if (note.hashtags.length > 0) {
    lines.push('---')
    lines.push('')
    lines.push('**话题标签：**')
    lines.push('')
    lines.push(note.hashtags.map(tag => `\`#${tag}\``).join(' '))
  }
  
  // 元信息
  lines.push('')
  lines.push('---')
  lines.push('')
  lines.push(`> 生成时间：${new Date(note.created_at).toLocaleString('zh-CN')}`)
  lines.push(`> 风格：${note.style}`)
  
  return lines.join('\n')
}

// ============================================================
// ZIP 导出
// ============================================================

/**
 * 导出单篇笔记为 ZIP
 */
export async function exportNoteAsZip(
  note: Note,
  images: Array<{ url: string; filename: string }>,
  options: ExportOptions = {},
  onProgress?: (progress: ExportProgress) => void
): Promise<Blob> {
  const {
    includeText = true,
    includeImages = true,
    includeShareImage = false,
    textFormat = 'txt',
  } = options

  const zip = new JSZip()
  const total = (includeText ? 1 : 0) + (includeImages ? images.length : 0) + (includeShareImage && options.shareImageDataUrl ? 1 : 0)
  let current = 0

  // 添加文案
  if (includeText) {
    const textContent = textFormat === 'md' 
      ? generateNoteMarkdown(note) 
      : generateNoteText(note)
    const filename = `文案.${textFormat}`
    zip.file(filename, textContent)
    current++
    onProgress?.({ current, total, message: '已添加文案' })
  }

  // 添加分享图
  if (options.includeShareImage && options.shareImageDataUrl) {
    const shareBlob = await dataUrlToBlob(options.shareImageDataUrl)
    const filename = options.shareImageFilename || '分享图.png'
    zip.file(filename, shareBlob)
    current++
    onProgress?.({ current, total, message: '已添加分享图' })
  }

  // 添加图片
  if (includeImages && images.length > 0) {
    for (let i = 0; i < images.length; i++) {
      const img = images[i]
      try {
        onProgress?.({ current, total, message: `正在下载图片 ${i + 1}/${images.length}...` })
        
        // 使用 Image + Canvas 绕过 fetch 的 CORS 限制
        const blob = await fetchImageAsBlob(img.url)
        const ext = getExtensionFromMime(blob.type) || 'jpg'
        const filename = img.filename || `图片_${String(i + 1).padStart(2, '0')}.${ext}`
        
        zip.file(filename, blob)
        current++
        onProgress?.({ current, total, message: `已添加图片 ${i + 1}/${images.length}` })
      } catch (error) {
        console.error(`下载图片失败: ${img.url}`, error)
        // 继续处理其他图片
      }
    }
  }

  onProgress?.({ current: total, total, message: '正在打包...' })
  return zip.generateAsync({ type: 'blob' })
}

/**
 * 批量导出多篇笔记为 ZIP
 */
export async function exportNotesAsZip(
  notes: ExportNote[],
  options: ExportOptions = {},
  onProgress?: (progress: ExportProgress) => void
): Promise<Blob> {
  const {
    includeText = true,
    includeImages = true,
    includeShareImage = false,
    textFormat = 'txt',
    folderPerNote = true,
  } = options

  const zip = new JSZip()
  
  // 计算总任务数
  let total = 0
  for (const { images } of notes) {
    total += (includeText ? 1 : 0) + (includeImages ? images.length : 0) + (includeShareImage ? 1 : 0)
  }
  let current = 0

  for (let noteIndex = 0; noteIndex < notes.length; noteIndex++) {
    const { note, images, shareImageDataUrl, shareImageFilename } = notes[noteIndex]
    const folderName = folderPerNote 
      ? `笔记${String(noteIndex + 1).padStart(2, '0')}_${sanitizeFilename(note.title.slice(0, 20))}`
      : ''

    // 添加文案
    if (includeText) {
      const textContent = textFormat === 'md' 
        ? generateNoteMarkdown(note) 
        : generateNoteText(note)
      const filename = folderPerNote 
        ? `${folderName}/文案.${textFormat}`
        : `笔记${noteIndex + 1}_文案.${textFormat}`
      zip.file(filename, textContent)
      current++
      onProgress?.({ current, total, message: `已添加笔记 ${noteIndex + 1} 文案` })
    }

    // 添加分享图
    if (includeShareImage && shareImageDataUrl) {
      const shareBlob = await dataUrlToBlob(shareImageDataUrl)
      const filename = folderPerNote
        ? `${folderName}/${shareImageFilename || '分享图.png'}`
        : `笔记${noteIndex + 1}_${shareImageFilename || '分享图'}.png`
      zip.file(filename, shareBlob)
      current++
      onProgress?.({ current, total, message: `已添加笔记 ${noteIndex + 1} 分享图` })
    }

    // 添加图片
    if (includeImages && images.length > 0) {
      for (let i = 0; i < images.length; i++) {
        const img = images[i]
        try {
          onProgress?.({ 
            current, 
            total, 
            message: `正在下载笔记 ${noteIndex + 1} 图片 ${i + 1}/${images.length}...` 
          })
          
          // 使用 Image + Canvas 绕过 fetch 的 CORS 限制
          const blob = await fetchImageAsBlob(img.url)
          const ext = getExtensionFromMime(blob.type) || 'jpg'
          const imgFilename = img.filename || `图片_${String(i + 1).padStart(2, '0')}.${ext}`
          const fullPath = folderPerNote 
            ? `${folderName}/${imgFilename}`
            : `笔记${noteIndex + 1}_${imgFilename}`
          
          zip.file(fullPath, blob)
          current++
          onProgress?.({ 
            current, 
            total, 
            message: `已添加笔记 ${noteIndex + 1} 图片 ${i + 1}/${images.length}` 
          })
        } catch (error) {
          console.error(`下载图片失败: ${img.url}`, error)
        }
      }
    }
  }

  onProgress?.({ current: total, total, message: '正在打包...' })
  return zip.generateAsync({ type: 'blob' })
}

/**
 * 导出并下载 ZIP 文件
 */
export async function downloadAsZip(
  notes: ExportNote[],
  filename: string,
  options?: ExportOptions,
  onProgress?: (progress: ExportProgress) => void
): Promise<void> {
  const blob = await exportNotesAsZip(notes, options, onProgress)
  downloadBlob(blob, filename)
}

// ============================================================
// 单文件下载
// ============================================================

/**
 * 下载单个文件
 */
export function downloadFile(
  content: string, 
  filename: string, 
  mimeType = 'text/plain;charset=utf-8'
): void {
  const blob = new Blob([content], { type: mimeType })
  downloadBlob(blob, filename)
}

/**
 * 下载 Blob 文件
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

/**
 * 批量下载图片（不推荐，浏览器可能拦截）
 */
export async function downloadImages(images: NoteImage[]): Promise<void> {
  for (const image of images) {
    if (image.url && image.status === 'completed') {
      try {
        // 使用 Image + Canvas 绕过 fetch 的 CORS 限制
        const blob = await fetchImageAsBlob(image.url)
        const ext = getExtensionFromMime(blob.type) || 'jpg'
        downloadBlob(blob, `image_${image.index + 1}.${ext}`)
        
        // 等待一下避免浏览器拦截
        await new Promise(resolve => setTimeout(resolve, 500))
      } catch (error) {
        console.error(`下载图片失败: ${image.url}`, error)
      }
    }
  }
}

// ============================================================
// 辅助函数
// ============================================================

/**
 * 从 MIME 类型获取文件扩展名
 */
function getExtensionFromMime(mimeType: string): string | null {
  const mimeMap: Record<string, string> = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'image/webp': 'webp',
    'image/svg+xml': 'svg',
  }
  return mimeMap[mimeType] || null
}

/**
 * 清理文件名（移除非法字符）
 */
function sanitizeFilename(name: string): string {
  return name
    .replace(/[<>:"/\\|?*]/g, '')  // 移除非法字符
    .replace(/\s+/g, '_')          // 空格替换为下划线
    .replace(/\.+/g, '.')          // 多个点替换为单个
    .trim()
}

// fetchImageAsBlob 函数已移至文件顶部（使用 Image + Canvas 避免 CORS 问题）

/**
 * 将 Data URL 转换为 Blob
 */
async function dataUrlToBlob(dataUrl: string): Promise<Blob> {
  const res = await fetch(dataUrl)
  return res.blob()
}
